/**
 * Room sound that the browser will not start on its own.
 *
 * Browsers refuse to play a page's audio before the person has interacted
 * with it (play() rejects with NotAllowedError). A room reached by
 * navigation alone, such as an auto-answered or prerendered one, can land
 * there, and the old AudioTrack retried three times and then gave up
 * silently: the call looked live and was mute. The block is now recorded in
 * `features/base/media`.autoplay so the room can offer "Turn on sound".
 *
 * Deliberately NOT `config.startSilent`: that mutes every remote audio track
 * for good, which is the opposite of what the person wants.
 */
import { AUDIO_AUTOPLAY_BLOCKED, AUDIO_AUTOPLAY_UNBLOCKED } from './actionTypes';
const INITIAL_AUTOPLAY_STATE = { audioBlocked: false };
export function audioAutoplayReducer(state = INITIAL_AUTOPLAY_STATE, action) {
    switch (action.type) {
        case AUDIO_AUTOPLAY_BLOCKED:
            return state.audioBlocked ? state : { audioBlocked: true };
        case AUDIO_AUTOPLAY_UNBLOCKED:
            return state.audioBlocked ? { audioBlocked: false } : state;
        default:
            return state;
    }
}
/** Whether the call's sound is currently blocked by the browser's autoplay policy. */
export function isAudioAutoplayBlocked(state) {
    return state['features/base/media']?.autoplay?.audioBlocked === true;
}
/** Whether a rejected play() is the browser's autoplay policy rather than a media fault. */
export function isAutoplayPolicyError(error) {
    return Boolean(error) && typeof error === 'object' && error.name === 'NotAllowedError';
}
/**
 * Records an autoplay block once per blocked episode: every remote audio
 * element fails the same way at about the same moment, and the first report
 * is the only one that carries information.
 */
export function reportAudioAutoplayBlocked() {
    return (dispatch, getState) => {
        if (!isAudioAutoplayBlocked(getState())) {
            dispatch({ type: AUDIO_AUTOPLAY_BLOCKED });
        }
    };
}
export function audioAutoplayUnblocked() {
    return { type: AUDIO_AUTOPLAY_UNBLOCKED };
}
/**
 * Calls play() on every audio element that carries a call stream, inside the
 * person's click, and reports whether all of them now play.
 *
 * Only elements with a srcObject are call audio (remote tracks are attached
 * as MediaStreams). Jitsi's notification sounds are file-backed <audio>
 * elements; replaying them here would fire every join/leave chime at once.
 * play() is called synchronously for each element before anything is
 * awaited, so every call happens within the click's user activation.
 */
export function resumeRoomAudio(root) {
    const elements = Array.from(root.querySelectorAll('audio'))
        .filter(element => Boolean(element.srcObject));
    const attempts = elements.map(element => {
        try {
            return Promise.resolve(element.play()).then(() => undefined, (error) => error || new Error('play'));
        }
        catch (error) {
            return Promise.resolve(error || new Error('play'));
        }
    });
    return Promise.all(attempts).then(errors => {
        const failures = errors.filter(Boolean);
        if (failures.length) {
            const name = failures[0]?.name;
            return {
                error: typeof name === 'string' && name ? name : 'Error',
                ok: false,
                played: elements.length - failures.length
            };
        }
        return { ok: true, played: elements.length };
    });
}
